import type {
  LocalAudioTrack,
  LocalTrack,
  Participant,
  Room,
  Track,
} from 'livekit-client'
import { BroadcastState } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { toConnectionQualityLevel } from '../MeetingCubit'
import { BroadcastMonitor } from './BroadcastMonitor'
import { TranscriptController } from './TranscriptController'
import { DeadAirController } from './DeadAirController'
import { SpeechReachabilityController } from './SpeechReachabilityController'
import { DeviceManager } from '../../util/DeviceManager'
import z from 'zod'
import { captureException, captureMessage } from '@sentry/core'
import { createAudioVolumeAnalyser } from '../../util/audioAnalysis'
import { ServerIdentities, ServerIdentitiesIds } from '../../util/identities'
import { attachSoundcheckToRoom } from './soundcheck/attachToRoom'
import {
  MeetingTelemetryEvent,
  disconnectReasonName,
  documentHidden,
  hashId,
  navigatorOnline,
  secondsSince,
} from '../../util/telemetry/meetingTelemetry'

/** The capture device behind a local track, when the browser reports one. */
function mediaStreamTrackDeviceId(track: LocalTrack): string | undefined {
  return track.mediaStreamTrack?.getSettings?.().deviceId
}

// Payload type mirroring the Go ModeratorDataMessage JSON schema
export type ModeratorDataMessage = {
  message?: string
  agentId?: string
  formElementId?: string
  requestId?: string
  requestSoloBotResponse?: boolean
  requestSoloBotInterrupt?: boolean
  sendToSoloBot?: boolean
}

/**
 * Encode a string to a non-shared Uint8Array for LiveKit's publishData, which
 * requires Uint8Array<ArrayBuffer> rather than the ArrayBufferLike-backed array
 * TextEncoder returns.
 */
function encodeData(value: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new TextEncoder().encode(value))
}

/**
 * The MeetingLivekitController class is responsible for communicating with Livekit
 * and updating the Meeting model with the relavant data.
 *
 * The idea is to break up the Meeting class into smaller, more manageable pieces.
 */
export class MeetingLivekitController {
  unsubscribers: (() => void)[] = []
  transcriptController: TranscriptController
  /** Inbound audio observability; inert unless `detectionEnabled` is on. */
  deadAirController: DeadAirController
  /** Outbound: does locally-detected speech reach the server's active-speaker detection. */
  speechReachabilityController: SpeechReachabilityController
  isSpeaking = false
  startedSpeakingAt: number | null = null
  durationCapturedSoFar: number = 0
  speechCaptureDebounce: NodeJS.Timeout | null = null
  localTrackAnalyzerCleanup?: () => void = undefined
  private soundcheckDisposer: (() => void) | null = null
  private audioTrackCheckTimer: ReturnType<typeof setTimeout> | null = null
  private audioTrackWasPreviouslyPublished: boolean = false
  private pageLeaving: boolean = false
  private onPageLeave = () => {
    this.pageLeaving = true
  }

  // The browser's own view of the network. LiveKit does not surface it, and
  // the dropped client's events may never reach GA, so these are what tell a
  // wifi drop from a server-side removal in the rows that do arrive.
  private offlineAt: number | null = null
  private onNetworkOffline = () => {
    this.offlineAt = Date.now()
    this.meeting.logEvent(MeetingTelemetryEvent.networkOffline, {
      room_state: this.livekitRoom?.state,
    })
  }

  private onNetworkOnline = () => {
    this.meeting.logEvent(MeetingTelemetryEvent.networkOnline, {
      seconds_offline: secondsSince(this.offlineAt),
      room_state: this.livekitRoom?.state,
    })
    this.offlineAt = null
  }

  // Connection bookkeeping read by the room_disconnected event. LiveKit hands
  // the disconnect handler a reason but nothing about what led up to it, and
  // the silent-disconnect triage had to reconstruct that from sidebar clicks.
  /** When `room.state` last entered `reconnecting` or `signalReconnecting`. */
  private lastReconnectingAt: number | null = null
  // One reconnect episode runs from the first `reconnecting` state to either
  // `reconnected` or `disconnected`. LiveKit keeps `room.state` at
  // `reconnecting` across its own retries, so `attempts` counts state entries
  // (signal-only then full, or a repeat after a brief `connected`), not the
  // SDK's internal tries.
  private reconnectEpisodeStartedAt: number | null = null
  private reconnectEpisodeAttempts = 0
  private connectedAt: number | null = null
  // Sentry gets one message per drop for an unexpected disconnect, so replays
  // are searchable by `lk_disconnect_reason` without a message per retry.
  // Reset when the room comes back, so a session that drops again later is
  // reported again: the repeat drop is the one worth a replay.
  private sentryDisconnectReported = false
  // Publish context. LiveKit's localTrackPublished does not say who asked for
  // the publish, so the caller notes it just before publishing and the handler
  // takes it. Best effort: a note nobody consumes expires so it cannot label a
  // later, unrelated publish.
  private pendingPublishTrigger: { trigger: string; at: number } | null = null
  private lastAudioPublishFailure: string | null = null
  private static readonly PUBLISH_TRIGGER_TTL_MS = 15_000
  /** How many `reconnected` events this controller has seen. */
  private reconnectCount = 0
  private previousConnectionState: string | null = null
  // After a disconnect nothing else logs: the health sample stops with the
  // room, and the next event is whatever the user clicks. The heartbeat is the
  // direct measure of how long a dropped user sat in the dead tab.
  private disconnectedHeartbeatTimer: ReturnType<typeof setInterval> | null =
    null

  private disconnectedAt: number | null = null
  private disconnectedHeartbeats = 0
  private static readonly DISCONNECTED_HEARTBEAT_INTERVAL_MS = 60_000
  private static readonly DISCONNECTED_HEARTBEAT_MAX_BEATS = 30

  private audioSilencePollTimer: ReturnType<typeof setTimeout> | null = null
  private audioSilenceStrikes: number = 0
  private audioSilencePollingActive: boolean = false
  private lastAudioHealthSampleAt: number = 0
  private static readonly AUDIO_SILENCE_POLL_INTERVAL_MS = 15_000
  private static readonly AUDIO_SILENCE_FAST_POLL_INTERVAL_MS = 5_000
  private static readonly AUDIO_SILENCE_STRIKE_THRESHOLD = 3
  private static readonly AUDIO_HEALTH_SAMPLE_MIN_INTERVAL_MS = 60_000

  constructor(protected meeting: MeetingCubit) {
    this.transcriptController = new TranscriptController(meeting)
    this.deadAirController = new DeadAirController(meeting)
    this.speechReachabilityController = new SpeechReachabilityController(
      meeting
    )
  }

  initialize() {
    this.transcriptController.initialize()
    this.speechReachabilityController.initialize()
    this.setupBroadcastMonitor()
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.onPageLeave)
      window.addEventListener('pagehide', this.onPageLeave)
      window.addEventListener('offline', this.onNetworkOffline)
      window.addEventListener('online', this.onNetworkOnline)
    }
  }

  dispose() {
    this.clearAudioTrackCheckTimer()
    this.stopAudioSilencePolling()
    this.stopDisconnectedHeartbeat()
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.onPageLeave)
      window.removeEventListener('pagehide', this.onPageLeave)
      window.removeEventListener('offline', this.onNetworkOffline)
      window.removeEventListener('online', this.onNetworkOnline)
    }
    for (const unsub of this.unsubscribers) {
      unsub()
    }
    this.soundcheckDisposer?.()
    this.soundcheckDisposer = null
    this.livekitRoom?.removeAllListeners()
    this.transcriptController.dispose()
    this.deadAirController.dispose()
    this.speechReachabilityController.dispose()
    this.broadcastMonitor?.detach()
    this.localTrackAnalyzerCleanup?.()
  }

  onSlideChange() {
    this.setupBroadcastMonitor()
    this.resetAckFailures()
    this.deadAirController.onSlideChange()
  }

  lastMessage: string = ''
  sendStreamStatus(
    status: LivekitStreamStatus,
    position: number,
    duration: number
  ) {
    if (!this.livekitRoom) return

    const slideId = this.meeting.currentSlide?.id

    if (!slideId) return

    const message = JSON.stringify({
      type: 6,
      status,
      duration: Math.round(duration),
      position: Math.round(position),
      identity: this.currentUser.uid,
      userId: this.currentUser.uid,
      slideId,
    })

    this.meeting.updateSlideStreamStatus(
      slideId,
      this.currentUser.uid,
      status,
      position,
      duration
    )

    if (this.lastMessage === message) return

    this.lastMessage = message

    const participantArray = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
    const groupLeaders = participantArray.filter((p) =>
      this.meeting.groupLeaderUserIds.includes(p.identity)
    )

    if (groupLeaders.length === 0) return

    this.livekitRoom.localParticipant?.publishData(encodeData(message), {
      reliable: true,
      destinationIdentities: groupLeaders.map((p) => p.identity),
    })
  }

  sendDragPosition(data: {
    id: string
    position: {
      x: number | undefined
      y: number | undefined
      anchor?: string
    }
    isDragged: boolean
    label?: string | undefined
    userId?: string
    placeholder?: boolean
  }) {
    if (!this.livekitRoom) return

    const message = JSON.stringify({
      type: LivekitMessageType.dragPosition,
      ...data,
    })

    const everyOneButMe = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
      .filter((p) => p.identity !== this.currentUser.uid)
      .filter((p) => p.identity !== ServerIdentities.BreakoutLearning)
      .filter((p) => p.identity !== ServerIdentities.Moderator)
      .filter((p) => p.identity !== ServerIdentities.Secretary)
      .map((p) => p.identity)

    this.livekitRoom.localParticipant
      ?.publishData(encodeData(message), {
        // if isDragged is true, we don't need reliable delivery
        // because we are sending it frequently, but if it's false
        // we want to make sure it gets delivered
        reliable: data.isDragged ? false : true,
        destinationIdentities: everyOneButMe,
      })
      .catch(() => {
        // console.error('Failed to send drag items update')
      })
  }

  sendDragListPosition(
    id: string,
    position: {
      idBefore: string | undefined
      placeholder?: boolean
      containerId: string | undefined
    }
  ) {
    if (!this.livekitRoom) return

    const message = JSON.stringify({
      type: LivekitMessageType.dragListPosition,
      id,
      position,
    })
    const everyOneButMe = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
      .filter((p) => p.identity !== this.currentUser.uid)
      .filter((p) => p.identity !== ServerIdentities.BreakoutLearning)
      .filter((p) => p.identity !== ServerIdentities.Moderator)
      .filter((p) => p.identity !== ServerIdentities.Secretary)
      .map((p) => p.identity)

    this.livekitRoom.localParticipant
      ?.publishData(encodeData(message), {
        reliable: true,
        destinationIdentities: everyOneButMe,
      })
      .catch(() => {
        // console.error('Failed to send drag items update')
      })
  }

  sendDragMousePosition(
    id: string,
    position: { x: number | undefined; y: number | undefined; anchor?: string }
  ) {
    if (!this.livekitRoom) return

    const message = JSON.stringify({
      type: LivekitMessageType.dragMousePosition,
      id,
      position,
    })
    const everyOneButMe = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
      .filter((p) => p.identity !== this.currentUser.uid)
      .filter((p) => p.identity !== ServerIdentities.BreakoutLearning)
      .filter((p) => p.identity !== ServerIdentities.Moderator)
      .filter((p) => p.identity !== ServerIdentities.Secretary)
      .map((p) => p.identity)

    this.livekitRoom.localParticipant
      ?.publishData(encodeData(message), {
        reliable: false,
        destinationIdentities: everyOneButMe,
      })
      .catch(() => {
        // console.error('Failed to send drag mouse position')
      })
  }

  sendSoloBotResponseRequest() {
    if (!this.livekitRoom) return
    const canSend = this.meeting.inSoloBotMode

    if (canSend) {
      const payload: ModeratorDataMessage = { requestSoloBotResponse: true }
      this.livekitRoom.localParticipant?.publishData(
        encodeData(JSON.stringify(payload)),
        {
          reliable: true,
          destinationIdentities: [ServerIdentities.Moderator],
        }
      )
    }
  }

  /**
   * Send a text message to the Solo Bot via the Moderator. Optionally request a spoken response.
   */
  sendMessageToSoloBotRequest(
    message: string,
    options?: {
      requestSoloBotResponse?: boolean
      requestSoloBotInterrupt?: boolean
      agentId?: string
      formElementId?: string
      requestId?: string
      sendToSoloBot?: boolean
    }
  ) {
    if (!this.livekitRoom) return
    const canSend = this.meeting.inSoloBotMode
    if (!canSend) return

    const payload: ModeratorDataMessage = {
      message,
      sendToSoloBot: options?.sendToSoloBot ?? false,
      requestSoloBotResponse: options?.requestSoloBotResponse ?? false,
      requestSoloBotInterrupt: options?.requestSoloBotInterrupt ?? false,
      ...(options?.agentId ? { agentId: options.agentId } : {}),
      ...(options?.formElementId
        ? { formElementId: options.formElementId }
        : {}),
      ...(options?.requestId ? { requestId: options.requestId } : {}),
    }

    // this.livekitRoom.localParticipant?.publishData(
    //   encodeData(JSON.stringify(payload)),
    //   {
    //     reliable: true,
    //     destinationIdentities: [ServerIdentities.Moderator],
    //   }
    // )
    const _options = {
      topic: `moderator-${this.currentUser.uid}`,
      destinationIdentities: [ServerIdentities.Moderator],
    }
    this.livekitRoom.localParticipant?.sendText(
      JSON.stringify(payload),
      _options
    )
  }

  sendBroadcastMessage(message: string) {
    // only internal users and group leaders can send broadcast messages
    const canSend =
      this.currentUser.isInternal || this.meeting.currentUserIsGroupLeader

    if (!canSend) {
      this.meeting.logEvent('meeting_broadcast_message_send_denied', {
        message,
        reason: 'canSendIsFalse',
        isInternal: this.currentUser.isInternal,
        isGroupLeader: this.meeting.currentUserIsGroupLeader,
      })
    }

    if (!this.livekitRoom) {
      this.meeting.logEvent('meeting_broadcast_message_send_denied', {
        message,
        reason: 'livekitRoomMissing',
      })
      return
    }

    if (message === 'pause') {
      this.meeting.setBroadcastState(BroadcastState.paused)
    } else if (message === 'stop') {
      this.meeting.setBroadcastState(BroadcastState.stopped)
    } else if (message === 'play') {
      this.meeting.setBroadcastState(BroadcastState.playing)
    }

    this.addAcknowledgeListener(message)

    this.livekitRoom.localParticipant
      ?.publishData(encodeData(message), {
        reliable: true,
        destinationIdentities: [ServerIdentities.BreakoutLearning],
      })
      .then(() => {
        this.meeting.logEvent('meeting_broadcast_message_sent')
      })
      .catch((e) => {
        const err = e as Error
        this.meeting.logEvent('meeting_broadcast_message_send_error', {
          error: err.name,
          message: err.message,
        })
      })
  }

  sendParticipantMessage(participantUserId: string, message: 'muteMic') {
    // only admins and group leaders can send broadcast messages
    const canSend =
      this.currentUser.isCorre || this.meeting.currentUserAllowedToMute

    if (!canSend) return

    if (!this.livekitRoom) return

    if (message === 'muteMic') {
      const payload = JSON.stringify({
        type: LivekitMessageType.muteMic,
      })
      this.livekitRoom.localParticipant?.publishData(encodeData(payload), {
        reliable: true,
        destinationIdentities: [participantUserId],
      })
    }
  }

  /**
   * Send a beforeSlideChange message to all other participants via LiveKit
   */
  sendBeforeSlideChange(slideId: string) {
    // only admins and group leaders can change slides, so only they can send this message
    const canSend =
      this.meeting.currentUserIsGroupLeader || this.meeting.currentUser.isAdmin

    if (!canSend) return
    if (!this.livekitRoom) return

    const everyOneButMe = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
      .filter((p) => p.identity !== this.currentUser.uid)
      .filter((p) => p.identity !== ServerIdentities.BreakoutLearning)
      .filter((p) => p.identity !== ServerIdentities.Moderator)
      .filter((p) => p.identity !== ServerIdentities.Secretary)
      .map((p) => p.identity)

    const message = JSON.stringify({
      type: LivekitMessageType.beforeSlideChange,
      slideId,
      userId: this.currentUser.uid,
    })

    this.livekitRoom.localParticipant
      ?.publishData(encodeData(message), {
        reliable: true,
        destinationIdentities: everyOneButMe,
      })
      .catch(() => {
        // console.error('Failed to send beforeSlideChange message')
      })
  }

  sendStreamPauseMessage() {
    // only admins and group leaders can send broadcast messages
    const canSend = this.meeting.currentUserIsGroupLeader

    if (!canSend) return

    if (!this.livekitRoom) return

    const allParticipantsIds = Array.from(
      this.livekitRoom.remoteParticipants.values()
    ).map((p) => p.identity)

    const payload = JSON.stringify({
      type: LivekitMessageType.streamPause,
    })
    this.livekitRoom.localParticipant?.publishData(encodeData(payload), {
      reliable: true,
      destinationIdentities: allParticipantsIds,
    })
  }

  updateMetadata() {
    if (!this.livekitRoom) return
    if (!this.livekitRoom.localParticipant) return
    const currentMetadata = this.livekitRoom.localParticipant.metadata || '{}'
    const parsed = JSON.parse(currentMetadata)
    parsed['info'] = 'test'
    const serialized = JSON.stringify(parsed)
    this.livekitRoom.localParticipant.setMetadata(serialized)
  }

  seekBroadcast(position: number) {
    if (this.livekitRoom) {
      this.meeting.setBroadcastPosition(position + 3)
      this.sendBroadcastMessage(`seek|${position + 3}`)
    }
  }

  private _ackListeners: Record<string, NodeJS.Timeout> = {}
  /**
   * Add an acknowledge listener for a given action
   *
   * It will wait 5 seconds for an ack to be received, and if it isn't
   * it will log an error
   *
   * @param actionString string
   */
  addAcknowledgeListener(actionString: string) {
    // capture which slide we are on when we send the message
    const slide = this.meeting.roomState.data.activeSlide

    // seek messages are special, they are sent with a position
    // so we need to strip that out - we don't care about the position
    // we just care about the action
    const action = actionString.split('|')[0]

    this.meeting.logEvent('meeting_ack_listener', { action })

    this.clearAcknowledgeListener(action)

    this._ackListeners[action] = setTimeout(() => {
      // if we haven't received an ack, we send a Sentry error
      captureException(new Error(`No ack received for ${action}`))
      this.meeting.logEvent('meeting_ack_failure', {
        action,
        slide,
      })
      this.handleAcknowledgeFailure(action)
      delete this._ackListeners[action]
    }, 5000)
  }

  private subsequentAckFailures: Record<string, number> = {}
  /**
   * Handle an acknowledge message from the broadcast
   *
   * @param action string
   */
  handleAcknowledge(action: string) {
    this.subsequentAckFailures[action] = 0

    this.meeting.logEvent('meeting_ack_received', { action })
    this.clearAcknowledgeListener(action)
  }

  handleAcknowledgeFailure(action: string) {
    if (!this.subsequentAckFailures[action])
      this.subsequentAckFailures[action] = 0

    this.subsequentAckFailures[action]++

    const failureCount = this.subsequentAckFailures[action]

    // if play action failed, revert to paused state so someone can try again
    if (action === 'play') {
      this.meeting.setBroadcastState(BroadcastState.paused)
    }

    // if we sent a play/pause message and no ack, we should stop the broadcast, because that
    // means the broadcast is in a bad state, so we'll need to restart it
    if (failureCount >= 3 && (action === 'play' || action === 'pause')) {
      this.meeting.setBroadcastState(BroadcastState.stopped)
    }
  }

  resetAckFailures() {
    this.subsequentAckFailures = {}
  }

  /**
   * Clear the acknowledge listener for a given action
   *
   * This is safe to run even if the listener doesn't exist
   *
   * @param action string
   */
  clearAcknowledgeListener(action: string) {
    if (this._ackListeners[action]) {
      clearTimeout(this._ackListeners[action])
      delete this._ackListeners[action]
    }
  }

  setupDone = false
  setupLivekitListeners() {
    const room = this.livekitRoom
    if (!room) return
    if (this.setupDone) return

    this.deadAirController.attach(room)

    // Soundcheck observes the same room beside the existing checks. It owns its
    // own subscriptions and returns one disposer.
    this.soundcheckDisposer = attachSoundcheckToRoom(
      room,
      this.meeting,
      this.meeting.soundcheck
    )

    room.on('mediaDevicesChanged', async () => {
      let devices: MediaDeviceInfo[] = []
      try {
        devices = await DeviceManager.getLocalDevices()
      } catch (e) {
        if (
          (e instanceof Error || e instanceof DOMException) &&
          e.name === 'NotFoundError'
        ) {
          // getUserMedia is called internally when no labelled devices are found.
          // NotFoundError means no camera/mic exists — proceed with empty list so
          // the audioInputDevices.length === 0 / videoInputDevices.length === 0
          // paths below can run and log appropriately.
        } else {
          captureException(e)
          return
        }
      }

      const activeAudioInput = room.getActiveDevice('audioinput')
      if (activeAudioInput) {
        const audioInputDevices = devices.filter((d) => d.kind === 'audioinput')
        const found = audioInputDevices.find(
          (d) => d.deviceId === activeAudioInput
        )
        if (!found && audioInputDevices.length > 0) {
          this.meeting.logEvent(MeetingTelemetryEvent.audioInputSwitched, {
            reason: 'device_vanished',
            from_device_id_hash: hashId(activeAudioInput),
            to_device_id_hash: hashId(audioInputDevices[0].deviceId),
            remaining_audio_inputs: audioInputDevices.length,
            room_state: room.state,
          })
          room.switchActiveDevice('audioinput', audioInputDevices[0].deviceId)
        }
        if (audioInputDevices.length === 0) {
          this.meeting.repository.logEvent('no_audio_input_devices')
        }
      }

      const activeAudioOutput = room.getActiveDevice('audiooutput')
      if (activeAudioOutput) {
        const audioOutputDevices = devices.filter(
          (d) => d.kind === 'audiooutput'
        )
        const found = audioOutputDevices.find(
          (d) => d.deviceId === activeAudioOutput
        )
        if (!found && audioOutputDevices.length > 0) {
          room.switchActiveDevice('audiooutput', audioOutputDevices[0].deviceId)
        }
        if (audioOutputDevices.length === 0) {
          this.meeting.repository.logEvent('no_audio_output_devices')
        }
      }

      const activeVideoInput = room.getActiveDevice('videoinput')
      if (activeVideoInput) {
        const videoInputDevices = devices.filter((d) => d.kind === 'videoinput')
        const found = videoInputDevices.find(
          (d) => d.deviceId === activeVideoInput
        )
        if (!found && videoInputDevices.length > 0) {
          room.switchActiveDevice('videoinput', videoInputDevices[0].deviceId)
        }
        if (videoInputDevices.length === 0) {
          this.disableVideo()
          this.meeting.repository.logEvent('no_video_output_devices')
        }
      }
    })

    room.on('trackMuted', (publication, participant) => {
      if (participant.identity === this.currentUser.uid) {
        this.meeting.logEvent('room_track_muted', {
          track_kind: publication.kind,
        })
      }
    })
    room.on('trackUnmuted', (publication, participant) => {
      if (participant.identity === this.currentUser.uid) {
        this.meeting.logEvent('room_track_unmuted', {
          track_kind: publication.kind,
        })
      }
    })

    room.on('activeSpeakersChanged', (p) => this.onActiveSpeakersChanged(p))

    room.on('participantConnected', (p) => {
      this.meeting.addUserConnectionTime(p.identity, Date.now())
      this.updateParticipantIds()
      this.setupBroadcastMonitor()
    })

    room.on('participantDisconnected', (participant) => {
      // LiveKit removes the participant from the map before emitting, so the
      // size here is the room after the drop.
      const joinedAt =
        participant.joinedAt?.getTime() ??
        this.meeting.userConnectionTimes.get(participant.identity)
      this.meeting.logEvent(MeetingTelemetryEvent.participantDisconnected, {
        participant_id_hash: hashId(participant.identity),
        is_server_participant: ServerIdentitiesIds.includes(
          participant.identity
        ),
        seconds_in_room: secondsSince(joinedAt),
        remaining_participants: room.remoteParticipants.size,
      })

      // unsubscribe from the participant's tracks
      for (const publication of participant.trackPublications.values()) {
        publication.setSubscribed(false)
      }

      this.updateParticipantIds()
      this.setupBroadcastMonitor()
    })

    room.on('trackSubscriptionFailed', async (trackId, participant, error) => {
      const allTrackSubscriptions = Array.from(
        participant.trackPublications.values()
      )
      // capture all track subscriptions for that participant
      const knownTracks = allTrackSubscriptions
        .filter((p) => [p.trackSid, p.kind, p.subscriptionStatus].join(':'))
        .join(', ')

      this.deadAirController.noteSubscriptionFailed(trackId)

      this.meeting.logEvent('room_track_subscription_failed', {
        trackId,
        participantId: participant.identity,
        connectionStatus: room.state,
        participantPublicationCount: participant.trackPublications.size,
        participantConnectionQuality: participant.connectionQuality,
        userConnectionQuality: room.localParticipant?.connectionQuality,
        subscribedTracks: knownTracks,
        error: error,
      })
    })
    room.on('connectionQualityChanged', (quality, participant) => {
      if (participant.isLocal) {
        this.meeting.setLocalConnectionQuality(
          toConnectionQualityLevel(quality)
        )
        this.speechReachabilityController.noteLocalConnectionQuality(quality)
        this.meeting.logEvent('room_connection_local_quality_changed', {
          quality: quality,
        })
      } else {
        this.meeting.logEvent('room_connection_remote_quality_changed', {
          quality: quality,
          participantId: participant.identity,
        })
      }
    })
    room.on('connectionStateChanged', (state) => {
      this.speechReachabilityController.noteConnectionState(state)
      if (state === 'reconnecting' || state === 'signalReconnecting') {
        this.lastReconnectingAt = Date.now()
        if (this.reconnectEpisodeStartedAt === null) {
          this.reconnectEpisodeStartedAt = this.lastReconnectingAt
        }
        this.reconnectEpisodeAttempts++
        this.meeting.logEvent(MeetingTelemetryEvent.roomReconnecting, {
          signal_only: state === 'signalReconnecting',
          attempt: this.reconnectEpisodeAttempts,
          navigator_online: navigatorOnline(),
          document_hidden: documentHidden(),
        })
      }
      this.meeting.logEvent(MeetingTelemetryEvent.roomConnectionStateChanged, {
        state: state,
        previous_state: this.previousConnectionState,
        navigator_online: navigatorOnline(),
      })
      // Expose the raw state so the banner can detect a hard network drop:
      // livekit's connectionQualityChanged only reflects server-reported
      // quality, so a wifi drop never yields 'lost' on its own.
      this.meeting.setConnectionState(state)
      this.previousConnectionState = state
    })
    room.on('trackSubscribed', (track, _, participant) => {
      this.deadAirController.noteSubscribed(track.sid)
      this.meeting.logEvent('meeting_track_subscribed', {
        trackSid: track.sid,
        kind: track.kind,
        participantId: participant.identity,
      })
      this.updatePublishedParticipants()
    })
    room.on('trackUnsubscribed', (track, _, participant) => {
      this.meeting.logEvent('meeting_track_unsubscribed', {
        trackSid: track.sid,
        kind: track.kind,
        participantId: participant.identity,
      })
      this.updatePublishedParticipants()
    })
    room.on('trackPublished', (publication, participant) => {
      const track = publication.track
      if (track) {
        this.meeting.logEvent('meeting_track_published', {
          trackSid: track.sid,
          kind: track.kind,
          participantId: participant.identity,
        })
      }
      this.updatePublishedParticipants()
    })
    room.on('trackUnpublished', (publication, participant) => {
      const track = publication.track
      if (track) {
        this.meeting.logEvent('meeting_track_unpublished', {
          trackSid: track.sid,
          kind: track.kind,
          participantId: participant.identity,
        })
      }
      this.meeting.resetPublishedParticipants()
      this.updatePublishedParticipants()
    })

    room.on('localTrackPublished', async (pub, participant) => {
      this.updatePublishedParticipants()
      const track = pub.track as LocalTrack<Track.Kind.Audio>
      const isAudio = pub.track?.kind === 'audio'
      // A publish the SFU never hears is the one made while the signal
      // connection is down; `room_state` at publish time is what proves it.
      const publishContext = {
        room_state: room.state,
        published_during_reconnect:
          room.state === 'reconnecting' || room.state === 'signalReconnecting',
        reconnect_count: this.reconnectCount,
        trigger: isAudio ? this.takePublishTrigger() : undefined,
      }

      this.meeting.logEvent(MeetingTelemetryEvent.localTrackPublished, {
        trackSid: track.sid,
        kind: track.kind,
        participantId: participant.identity,
        ...publishContext,
      })

      if (isAudio) {
        this.meeting.setLocalAudioTrackPublished(true)
        this.audioTrackWasPreviouslyPublished = true
        // A mic that landed makes the earlier failure history: a later
        // meeting_audio_track_failed must not blame it.
        this.lastAudioPublishFailure = null
        this.clearAudioTrackCheckTimer()
        this.startAudioSilencePolling()
        this.meeting.logEvent(MeetingTelemetryEvent.audioTrackPublished, {
          trackSid: track.sid,
          ...publishContext,
        })
      }

      // we use a string to avoid importing livekit-client package
      if (pub.track?.kind !== 'audio') return

      let localMicSpeechStartedAt: number | null = null
      let isLocalMicSpeaking = false
      let wasMutedOnStart = false

      const debugAudioAnalysis = false

      const cleanup = await createAudioVolumeAnalyser({
        track,
        debug: debugAudioAnalysis,
        onThresholdCross: (thresholdCrossedOver) => {
          if (thresholdCrossedOver && !isLocalMicSpeaking) {
            if (debugAudioAnalysis) {
              // eslint-disable-next-line no-console
              console.log('[audioAnalysis] Speech started')
            }
            // Speech started, set markers
            localMicSpeechStartedAt = Date.now()
            isLocalMicSpeaking = true
            wasMutedOnStart = track.isMuted
            this.meeting.setIsCurrentUserSpeaking(true)
          } else if (!thresholdCrossedOver && isLocalMicSpeaking) {
            if (debugAudioAnalysis) {
              // eslint-disable-next-line no-console
              console.log('[audioAnalysis] Speech stopped')
            }
            // Speech ended, log and reset
            if (localMicSpeechStartedAt) {
              const duration = Date.now() - localMicSpeechStartedAt!
              // Log to analytics, include info on mute status
              this.meeting.logEvent('meeting_user_spoke_locally', {
                duration: duration,
                isMuted: track.isMuted,
                wasMutedOnStart,
              })
              this.speechReachabilityController.recordLocalSpeech(
                duration,
                wasMutedOnStart,
                track.isMuted
              )
              if (!track.isMuted) {
                this.meeting.onUserStoppedSpeaking(duration)
              }
            }
            wasMutedOnStart = false
            localMicSpeechStartedAt = null
            isLocalMicSpeaking = false
            this.meeting.setIsCurrentUserSpeaking(false)
          }

          // is muted logic
          if (track.isMuted) {
            this.meeting.setSpeakingWhileMuted(thresholdCrossedOver)
          }
        },
      })

      const onTrackMute = () => {
        if (isLocalMicSpeaking) this.meeting.setSpeakingWhileMuted(true)
      }
      const onTrackUnmute = () => {
        this.meeting.setSpeakingWhileMuted(false)
      }
      // The underlying MediaStreamTrack ending (device unplugged, permission
      // revoked, OS grabbed the mic) is distinct from LiveKit unpublishing the
      // track — it tells us the capture died at the device, not in the room.
      const onTrackEnded = () => {
        this.meeting.logEvent(MeetingTelemetryEvent.localAudioTrackEnded, {
          track_id: track.mediaStreamTrack?.id,
          trackSid: track.sid,
          media_stream_track_ready_state: track.mediaStreamTrack?.readyState,
          device_id_hash: hashId(mediaStreamTrackDeviceId(track)),
          document_hidden: documentHidden(),
          track_muted: track.isMuted,
          room_state: room.state,
        })
      }
      // LiveKit swaps the MediaStreamTrack under the same publication when it
      // restarts capture (its own recovery within seconds of `ended`, a device
      // switch, an unmute on a dead capture). The publication and sid survive,
      // so without this the re-acquire is invisible.
      let lastKnownMediaStreamTrackId = track.mediaStreamTrack?.id
      const onTrackRestarted = () => {
        const previous = lastKnownMediaStreamTrackId
        lastKnownMediaStreamTrackId = track.mediaStreamTrack?.id
        this.meeting.logEvent(MeetingTelemetryEvent.localAudioTrackRestarted, {
          trackSid: track.sid,
          previous_track_id: previous,
          track_id: lastKnownMediaStreamTrackId,
          device_id_hash: hashId(mediaStreamTrackDeviceId(track)),
          room_state: room.state,
          reconnect_count: this.reconnectCount,
        })
      }

      track.on('muted', onTrackMute)
      track.on('unmuted', onTrackUnmute)
      track.on('ended', onTrackEnded)
      track.on('restarted', onTrackRestarted)

      if (this.localTrackAnalyzerCleanup) this.localTrackAnalyzerCleanup()

      this.localTrackAnalyzerCleanup = () => {
        cleanup()
        track.off('muted', onTrackMute)
        track.off('unmuted', onTrackUnmute)
        track.off('ended', onTrackEnded)
        track.off('restarted', onTrackRestarted)
      }
    })

    room.on('localTrackUnpublished', async (pub, participant) => {
      this.meeting.setIsCurrentUserSpeaking(false)
      const track = pub.track as LocalTrack<Track.Kind.Audio>

      this.meeting.logEvent(MeetingTelemetryEvent.localTrackUnpublished, {
        trackSid: track.sid,
        kind: track.kind,
        participantId: participant.identity,
      })

      if (pub.track?.kind === 'audio') {
        const remaining =
          this.livekitRoom?.localParticipant.audioTrackPublications.size ?? 0
        if (remaining === 0) {
          this.meeting.setLocalAudioTrackPublished(false)
          this.stopAudioSilencePolling()
          this.meeting.logEvent(MeetingTelemetryEvent.audioTrackLost, {
            trackSid: pub.track?.sid,
          })
          // Only treat this as a real mid-session loss if the page isn't
          // about to leave and the room hasn't started reconnecting.
          // LiveKit's handleDisconnect unpublishes tracks BEFORE flipping
          // room.state to Disconnected (the state-change is in the `finally`
          // block after the unpublish loop), so we can't rely on state alone.
          // The pageLeaving flag is set from beforeunload/pagehide — the
          // same events LiveKit's own onPageLeave hooks into.
          //
          // `room.state` only covers reconnects LiveKit starts on its own. A
          // reconnect this client asked for (`MeetingCubit.reconnectRoom`, run
          // by the dead-air nudge and the media troubleshooter) goes through
          // `disconnect()`, and at this point the room still reads as
          // connected. The cubit's flag is the only thing that tells the two
          // apart, and without it every such reconnect raised this dialog.
          const reconnecting =
            room.state === 'reconnecting' ||
            room.state === 'signalReconnecting' ||
            this.meeting.clientReconnectInFlight
          if (
            this.audioTrackWasPreviouslyPublished &&
            !this.pageLeaving &&
            !reconnecting
          ) {
            this.meeting.setShowAudioTrackMissingDialog(true)
          }
        }
      }
    })

    room.on('dataReceived', (data, sender) => {
      const decoded = new TextDecoder().decode(data)
      const senderIdentity = sender?.identity ?? 'unknown'

      if (decoded) {
        const { data, type } = this.parseLivekitMessage(decoded)
        if (type === LivekitMessageType.status) {
          if (senderIdentity !== ServerIdentities.BreakoutLearning) return

          const { status } = data
          if (status === LivekitStreamStatus.playing)
            this.meeting.setBroadcastState(BroadcastState.playing)
          if (status === LivekitStreamStatus.paused)
            this.meeting.setBroadcastState(BroadcastState.paused)
          if (status === LivekitStreamStatus.stopped)
            this.meeting.setBroadcastState(BroadcastState.stopped)

          this.meeting.logEvent('meeting_lk_message_status', { status })
        } else if (type === LivekitMessageType.duration) {
          if (senderIdentity !== ServerIdentities.BreakoutLearning) return

          this.meeting.logEvent('meeting_lk_message_duration', {
            duration: data.duration,
          })

          const { duration } = data
          this.meeting.setBroadcastDuration(duration)
          // duration
        } else if (type === LivekitMessageType.position) {
          if (senderIdentity !== ServerIdentities.BreakoutLearning) return
          // We DO NOT send a logEvent here by design - it's too noisy

          // if we got duration/position on a non-video slide, we should stop the broadcast
          if (!this.meeting.currentSlide?.isVideoSlide) {
            console.error(
              'Broadcast detected on non-video slide, stopping broadcast'
            )
            this.sendBroadcastMessage('stop')
          }
          const { position, duration } = data
          // if we receive a position message then we must be playing
          // the user missed the play message (this can happen if the user joins late)
          // or livekit screws it up
          this.meeting.setBroadcastState(BroadcastState.playing)
          this.meeting.setBroadcastPositionAndDuration(position, duration)
        } else if (type === LivekitMessageType.transcript) {
          // only accept transcript messages from the Secretary or the Moderator
          if (
            senderIdentity !== ServerIdentities.Secretary &&
            senderIdentity !== ServerIdentities.Moderator
          ) {
            return
          }

          // A transcript of the local user's speech means the server heard
          // them, whatever the active-speaker signal did or didn't deliver.
          if (data.identity === this.currentUser.uid) {
            this.speechReachabilityController.recordTranscriptConfirmation(
              data.id
            )
          }

          this.transcriptController.handleMessage(data)
        } else if (type === LivekitMessageType.unknown) {
          // should not happen
          captureException(new Error('Received unknown livekit message type'))
        } else if (type === 6) {
          const { status, position, duration, userId, slideId } = data
          if (!duration) return

          this.meeting.updateSlideStreamStatus(
            slideId,
            userId,
            status,
            position,
            duration
          )
        } else if (type === LivekitMessageType.streamPause) {
          this.meeting.logEvent('meeting_lk_message_pause')
          this.meeting.localCommands.emit('pauseVideo')
        } else if (type === LivekitMessageType.muteMic) {
          this.meeting.logEvent('meeting_lk_message_mutemic')
          this.mute()
        } else if (type === LivekitMessageType.acknowledge) {
          if (senderIdentity !== ServerIdentities.BreakoutLearning) return

          this.handleAcknowledge(data.action)
        } else if (type === LivekitMessageType.dragPosition) {
          const { id, position, isDragged, label, userId, placeholder } = data
          this.meeting.localCommands.emit('dragPosition', {
            id,
            position,
            isDragged,
            draggedBy: senderIdentity,
            label,
            userId,
            placeholder,
          })
        } else if (type === LivekitMessageType.dragListPosition) {
          const { id, position } = data
          this.meeting.localCommands.emit('dragListPosition', {
            id,
            position,
          })
        } else if (type === LivekitMessageType.dragMousePosition) {
          const { id, position } = data
          this.meeting.localCommands.emit('dragMousePosition', {
            id,
            position,
          })
        } else if (type === LivekitMessageType.beforeSlideChange) {
          const { slideId } = data
          // skip mine
          if (senderIdentity === this.currentUser.uid) return
          this.meeting.localCommands.emit('beforeSlideChange', {
            slideId,
          })
        } else if (
          type === LivekitMessageType.simpleModerationContributionUnder
        ) {
          const { slideId, identity } = data
          this.meeting.localCommands.emit('simpleModerationContributionUnder', {
            slideId,
            userId: identity,
          })
          this.meeting.logEvent('simple_moderation_contribution_under')
        } else if (
          type === LivekitMessageType.simpleModerationContributionOver
        ) {
          const { slideId, identity } = data
          this.meeting.localCommands.emit('simpleModerationContributionOver', {
            slideId,
            userId: identity,
          })
          this.meeting.logEvent('simple_moderation_contribution_over')
        }
      }
    })
    room.on('disconnected', (reason) => {
      const reasonName = disconnectReasonName(reason)
      this.meeting.logEvent(MeetingTelemetryEvent.roomDisconnected, {
        reason: reasonName,
        page_leaving: this.pageLeaving,
        document_hidden: documentHidden(),
        navigator_online: navigatorOnline(),
        client_reconnect_in_flight: this.meeting.clientReconnectInFlight,
        seconds_since_last_reconnecting: secondsSince(this.lastReconnectingAt),
        reconnect_count: this.reconnectCount,
        had_audio_publication: this.audioTrackWasPreviouslyPublished,
        audio_publication_present: this.hasLocalAudioPublication(),
      })
      this.tagSentryDisconnect(
        reasonName,
        this.reconnectEpisodeStartedAt !== null
      )
      if (this.reconnectEpisodeStartedAt !== null) {
        // CLIENT_INITIATED is the only reason `disconnect()` produces, so an
        // open episode ending with it was cut short by this client
        // (`reconnectRoom`, the QA tool, a reload), not by LiveKit's policy.
        this.meeting.logEvent(
          reasonName === 'CLIENT_INITIATED'
            ? MeetingTelemetryEvent.roomReconnectAborted
            : MeetingTelemetryEvent.roomReconnectGaveUp,
          {
            reason: reasonName,
            ...this.reconnectEpisodeFields(),
            client_reconnect_in_flight: this.meeting.clientReconnectInFlight,
            navigator_online: navigatorOnline(),
            document_hidden: documentHidden(),
          }
        )
        this.endReconnectEpisode()
      }
      this.clearAudioTrackCheckTimer()
      this.stopAudioSilencePolling()
      this.removeIsSpeakingListener()
      this.startDisconnectedHeartbeat(reasonName)
    })
    room.on('reconnected', () => {
      this.reconnectCount++
      const audioTrack = this.localAudioTrack()
      this.meeting.logEvent(MeetingTelemetryEvent.roomReconnected, {
        reconnect_count: this.reconnectCount,
        ...this.reconnectEpisodeFields(),
        audio_publication_present: this.hasLocalAudioPublication(),
        audio_track_ready_state: audioTrack?.mediaStreamTrack?.readyState,
        audio_track_muted: audioTrack?.isMuted,
      })
      this.endReconnectEpisode()
    })
    room.on('connected', () => {
      this.stopDisconnectedHeartbeat()
      this.sentryDisconnectReported = false
      this.connectedAt = Date.now()
      this.meeting.logEvent('room_connected')
      this.updateParticipantIds()
      this.setupBroadcastMonitor()
      this.addIsSpeakingListener()

      // sync active speakers on connect
      // we want active speakers to be correct if we join in progress room
      this.onActiveSpeakersChanged(room.activeSpeakers)

      const participants = Array.from(room.remoteParticipants.values())

      const breakoutParticipant = participants.find(
        (p) => p.identity === ServerIdentities.BreakoutLearning
      )

      if (breakoutParticipant) {
        // assume we are initialized, an event from the broadcast will update this
        this.meeting.setBroadcastState(BroadcastState.initialized)
      }
    })
  }

  // Called when the user enters the meeting phase (not on raw room
  // `connected`). With the shared Room, `connected` fires during AvCheck —
  // ~15s before the user actually joins — so anchoring this timer to it
  // would have it fire before mic publication is even attempted.
  startAudioTrackCheckTimer() {
    this.clearAudioTrackCheckTimer()
    this.audioTrackCheckTimer = setTimeout(() => {
      if (!this.meeting.localAudioTrackPublished) {
        this.meeting.setShowAudioTrackMissingDialog(true)
        this.meeting.logEvent(MeetingTelemetryEvent.audioTrackFailed, {
          gracePeriodMs: 15000,
          room_state: this.livekitRoom?.state,
          reconnect_count: this.reconnectCount,
          seconds_since_connected: secondsSince(this.connectedAt),
          mic_muted: this.isMuted,
          had_publication: this.audioTrackWasPreviouslyPublished,
          last_publish_error: this.lastAudioPublishFailure ?? undefined,
        })
      }
    }, 15_000)
  }

  private clearAudioTrackCheckTimer() {
    if (this.audioTrackCheckTimer) {
      clearTimeout(this.audioTrackCheckTimer)
      this.audioTrackCheckTimer = null
    }
  }

  private startAudioSilencePolling() {
    this.stopAudioSilencePolling()
    if (!this.meeting.repository.featureFlags.data.audioSilenceDetectionEnabled)
      return
    // Fresh poll session (e.g. after a rejoin): emit the first health sample
    // right away rather than waiting out the min-interval from the last session
    // — the rejoin case is exactly when the sample is most worth having.
    this.lastAudioHealthSampleAt = 0
    this.audioSilencePollingActive = true
    this.scheduleNextAudioSilenceCheck()
  }

  resumeAudioSilenceDetection() {
    // Re-arm polling after the user dismisses the silence dialog. No-op if
    // the audio track is gone; the next localTrackPublished will start it.
    if (!this.meeting.localAudioTrackPublished) return
    this.startAudioSilencePolling()
  }

  stopAudioSilencePolling() {
    this.audioSilencePollingActive = false
    if (this.audioSilencePollTimer) {
      clearTimeout(this.audioSilencePollTimer)
      this.audioSilencePollTimer = null
    }
    this.audioSilenceStrikes = 0
  }

  private scheduleNextAudioSilenceCheck() {
    if (!this.audioSilencePollingActive) return
    const delay =
      this.audioSilenceStrikes > 0
        ? MeetingLivekitController.AUDIO_SILENCE_FAST_POLL_INTERVAL_MS
        : MeetingLivekitController.AUDIO_SILENCE_POLL_INTERVAL_MS
    this.audioSilencePollTimer = setTimeout(async () => {
      this.audioSilencePollTimer = null
      await this.checkAudioSilence()
      this.scheduleNextAudioSilenceCheck()
    }, delay)
  }

  /**
   * The fields a failed mute/unmute/camera toggle is logged with: what the
   * room and the active device of that kind looked like at the time.
   */
  mediaFailureContext(kind: 'audioinput' | 'videoinput') {
    const room = this.livekitRoom
    let readyState: string | undefined
    if (kind === 'audioinput') {
      readyState = this.localAudioTrack()?.mediaStreamTrack?.readyState
    } else {
      for (const pub of room?.localParticipant.videoTrackPublications.values() ??
        []) {
        readyState = pub.track?.mediaStreamTrack?.readyState
        if (readyState) break
      }
    }
    return {
      room_state: room?.state,
      device_id_hash: hashId(room?.getActiveDevice(kind)),
      track_ready_state: readyState,
    }
  }

  private localAudioTrack(): LocalAudioTrack | undefined {
    const localParticipant = this.livekitRoom?.localParticipant
    if (!localParticipant) return undefined
    for (const pub of localParticipant.audioTrackPublications.values()) {
      if (pub.audioTrack) return pub.audioTrack
    }
    return undefined
  }

  private async checkAudioSilence() {
    if (!this.livekitRoom?.localParticipant) return
    const audioTrack = this.localAudioTrack()

    if (!audioTrack) {
      // Track is gone — nothing to assess. Reset; polling will be restarted
      // on next localTrackPublished anyway.
      this.audioSilenceStrikes = 0
      return
    }

    // Periodic health heartbeat: a low-rate snapshot of the local track state
    // over the session, so a mic that quietly degrades is legible without a
    // repro. Emitted from the poll tick (every 15s) but gated to once a minute,
    // and it bypasses markActivity so a silent session still times out.
    this.maybeLogAudioHealthSample(audioTrack)

    if (audioTrack.isMuted) {
      // Pause detection while muted — silence is expected, so we don't run
      // checkForSilence, but we also don't reset the strike count. A user
      // muting briefly on a broken mic should not lose accumulated strikes;
      // they'll resume on the next non-muted tick.
      return
    }

    if (typeof document !== 'undefined' && document.hidden) {
      // Safari (and to a lesser extent other browsers) throttle AudioContext
      // sampling in background tabs, which makes checkForSilence return true
      // for working mics. Skip without modifying strikes so detection
      // resumes accurately when the tab regains focus.
      return
    }

    const isSilent = await audioTrack.checkForSilence()
    if (!isSilent) {
      this.audioSilenceStrikes = 0
      return
    }

    this.audioSilenceStrikes++
    this.meeting.logEvent('room_local_audio_silence_detected', {
      strikes: this.audioSilenceStrikes,
      threshold: MeetingLivekitController.AUDIO_SILENCE_STRIKE_THRESHOLD,
    })

    if (
      this.audioSilenceStrikes >=
      MeetingLivekitController.AUDIO_SILENCE_STRIKE_THRESHOLD
    ) {
      this.meeting.setShowAudioTrackMissingDialog(true, 'silence')
      this.stopAudioSilencePolling()
    }
  }

  private maybeLogAudioHealthSample(audioTrack: LocalAudioTrack) {
    const now = Date.now()
    if (
      now - this.lastAudioHealthSampleAt <
      MeetingLivekitController.AUDIO_HEALTH_SAMPLE_MIN_INTERVAL_MS
    ) {
      return
    }
    this.lastAudioHealthSampleAt = now
    // logEventPassive keeps the shared envelope and dev-console print but skips
    // markActivity, so a silent user still times out.
    this.meeting.logEventPassive(MeetingTelemetryEvent.localAudioHealthSample, {
      track_muted: audioTrack.isMuted,
      track_ready_state: audioTrack.mediaStreamTrack?.readyState,
      publisher_connection_state: this.livekitRoom?.state,
      document_hidden:
        typeof document !== 'undefined' ? document.hidden : undefined,
      silence_strikes: this.audioSilenceStrikes,
    })
  }

  /**
   * Once a minute while the room stays disconnected, capped at 30 beats. Passive
   * so an idle user still times out. Not started when the page is leaving: the
   * tab is going away and the beats would never be sent. A disconnect this
   * client asked for still beats: `reason` and `client_reconnect_in_flight`
   * label it, and a `reconnectRoom` whose connect fails leaves the user just
   * as stuck.
   */
  private startDisconnectedHeartbeat(reason: string) {
    this.stopDisconnectedHeartbeat()
    if (this.pageLeaving) return
    // Read now, not per beat. `reconnectRoom` clears the flag in its
    // `finally` whether its connect landed or threw, and `connected` stops
    // this heartbeat, so by the first beat the live flag is always false and
    // carries nothing. The captured value does: a beat with it set can only
    // come from a `reconnectRoom` whose connect never reached `connected` —
    // the stranded user — because a connect that landed would have stopped
    // the beats.
    const clientReconnectInFlight = this.meeting.clientReconnectInFlight
    this.disconnectedAt = Date.now()
    this.disconnectedHeartbeats = 0
    this.disconnectedHeartbeatTimer = setInterval(() => {
      this.disconnectedHeartbeats++
      this.meeting.logEventPassive(
        MeetingTelemetryEvent.disconnectedHeartbeat,
        {
          reason,
          client_reconnect_in_flight: clientReconnectInFlight,
          beat: this.disconnectedHeartbeats,
          seconds_disconnected: secondsSince(this.disconnectedAt),
          document_hidden: documentHidden(),
          navigator_online: navigatorOnline(),
        }
      )
      if (
        this.disconnectedHeartbeats >=
        MeetingLivekitController.DISCONNECTED_HEARTBEAT_MAX_BEATS
      ) {
        this.stopDisconnectedHeartbeat()
      }
    }, MeetingLivekitController.DISCONNECTED_HEARTBEAT_INTERVAL_MS)
  }

  private stopDisconnectedHeartbeat() {
    if (this.disconnectedHeartbeatTimer) {
      clearInterval(this.disconnectedHeartbeatTimer)
      this.disconnectedHeartbeatTimer = null
    }
    this.disconnectedAt = null
  }

  /**
   * Label the next local audio publish (`trigger` on
   * meeting_audio_track_published). Call it right before the publish; the
   * note expires after 15 s so an unconsumed one cannot mislabel a later
   * publish that LiveKit makes on its own.
   */
  notePublishTrigger(trigger: string) {
    this.pendingPublishTrigger = { trigger, at: Date.now() }
  }

  /**
   * Forget a note whose publish never happened, so a publish LiveKit makes
   * on its own inside the 15 s window reads `unknown` instead of it. Only
   * clears the note for `trigger`: a later caller's note is theirs to keep.
   */
  clearPublishTrigger(trigger: string) {
    if (this.pendingPublishTrigger?.trigger === trigger) {
      this.pendingPublishTrigger = null
    }
  }

  /**
   * Note `trigger`, run the publish, and drop the note once the publish
   * settles. A publish that lands consumes the note before it resolves
   * (LiveKit emits `localTrackPublished` inside `publishTrack`), so the clear
   * only ever finds a note that no publish took: a rejected publish, or
   * `setMicrophoneEnabled(true)` on a mic that is already published, which
   * unmutes without publishing. Left in place, that note would label the
   * next publish LiveKit makes on its own inside the 15 s window.
   */
  private publishWithTrigger<T>(trigger: string, publish: () => Promise<T>) {
    this.notePublishTrigger(trigger)
    return publish().finally(() => {
      this.clearPublishTrigger(trigger)
    })
  }

  private takePublishTrigger(): string {
    const pending = this.pendingPublishTrigger
    this.pendingPublishTrigger = null
    if (!pending) return 'unknown'
    const age = Date.now() - pending.at
    return age > MeetingLivekitController.PUBLISH_TRIGGER_TTL_MS
      ? 'unknown'
      : pending.trigger
  }

  /**
   * Remember the last failed local audio create/publish so
   * meeting_audio_track_failed can say why no mic arrived. The events that log
   * the failure itself live in the wrapper, which owns the publish path.
   * `trigger` is the note the caller made for this publish; it is dropped the
   * same way `clearPublishTrigger` drops it, so a note another caller made in
   * the meantime (a reconnect that cut this publish short) is left for the
   * publish that follows. A caller that noted nothing passes nothing.
   */
  noteAudioPublishFailure(failure: {
    failure_stage: string
    error_name?: string
    trigger?: string
  }) {
    this.lastAudioPublishFailure = `${failure.failure_stage}:${
      failure.error_name ?? 'unknown'
    }`
    if (failure.trigger !== undefined) {
      this.clearPublishTrigger(failure.trigger)
    }
  }

  /**
   * Capture one Sentry message per drop for a disconnect the user did not
   * ask for, tagged with the reason so the replay is findable by it. The tag
   * is on the message only: setting it on the scope would put it on every
   * unrelated error the app captures for the rest of the session.
   * CLIENT_INITIATED is leaving, a reconnect this client asked for, or the QA
   * tool; none of those is a fault.
   */
  private tagSentryDisconnect(reason: string, gaveUp: boolean) {
    if (reason === 'CLIENT_INITIATED') return
    if (this.pageLeaving || this.meeting.clientReconnectInFlight) return
    if (this.sentryDisconnectReported) return
    this.sentryDisconnectReported = true
    captureMessage(
      gaveUp
        ? 'livekit reconnect gave up'
        : 'livekit disconnected unexpectedly',
      {
        level: 'warning',
        tags: { lk_disconnect_reason: reason },
        extra: {
          reconnect_count: this.reconnectCount,
          reconnect_attempts: this.reconnectEpisodeAttempts,
          navigator_online: navigatorOnline(),
        },
      }
    )
  }

  private reconnectEpisodeFields() {
    const startedAt = this.reconnectEpisodeStartedAt
    return {
      duration_ms: startedAt === null ? undefined : Date.now() - startedAt,
      attempts: this.reconnectEpisodeAttempts,
    }
  }

  private endReconnectEpisode() {
    this.reconnectEpisodeStartedAt = null
    this.reconnectEpisodeAttempts = 0
  }

  private hasLocalAudioPublication(): boolean {
    const size = this.livekitRoom?.localParticipant?.audioTrackPublications.size
    return typeof size === 'number' && size > 0
  }

  addIsSpeakingListener() {
    this.livekitRoom?.localParticipant.addListener(
      'isSpeakingChanged',
      this.handleIsSpeakingChanged
    )
  }

  removeIsSpeakingListener() {
    this.livekitRoom?.localParticipant.removeListener(
      'isSpeakingChanged',

      this.handleIsSpeakingChanged
    )
  }

  lastSpeechStart: number | null = null
  handleIsSpeakingChanged = (speaking: boolean) => {
    if (speaking && !this.lastSpeechStart) {
      this.lastSpeechStart = Date.now()
    }
    if (!speaking && this.lastSpeechStart) {
      const durationMs = Date.now() - this.lastSpeechStart
      this.transcriptController.addSpeechDuration(durationMs)
      this.lastSpeechStart = null
    }
  }

  updateParticipantIds() {
    if (!this.livekitRoom) return
    const participants = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
    const userIds = participants.map((p) => p.identity)

    this.meeting.updateParticipantIds(userIds)
  }

  updatePublishedParticipants() {
    if (!this.livekitRoom) return

    const remoteParticipants = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
    const publishedParticipantsIds = remoteParticipants
      .filter((p) => p.audioTrackPublications.size > 0)
      .map((p) => p.identity)

    const localParticipant = this.livekitRoom.localParticipant

    if (localParticipant.audioTrackPublications.size > 0) {
      // if the local participant is not muted, we should add them to the published participants
      publishedParticipantsIds.push(localParticipant.identity)
    }

    this.meeting.updatePublishedParticipantIds(publishedParticipantsIds)
  }

  mute() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    return this.livekitRoom?.localParticipant.setMicrophoneEnabled(false)
  }

  get isMuted() {
    return this.livekitRoom?.localParticipant.isMicrophoneEnabled === false
  }

  toggleAudio() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    const { localParticipant } = this.livekitRoom
    const currentState = localParticipant.isMicrophoneEnabled
    if (currentState) return localParticipant.setMicrophoneEnabled(false)
    return this.publishWithTrigger('toggle_audio', () =>
      localParticipant.setMicrophoneEnabled(true)
    )
  }

  unmute() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    const { localParticipant } = this.livekitRoom
    return this.publishWithTrigger('unmute', () =>
      localParticipant.setMicrophoneEnabled(true)
    )
  }

  isAudioEnabled() {
    return this.livekitRoom?.localParticipant.isMicrophoneEnabled
  }

  toggleVideo() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    const currentState = this.livekitRoom.localParticipant.isCameraEnabled
    return this.livekitRoom?.localParticipant.setCameraEnabled(!currentState)
  }

  toggleScreenShare() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    const currentState = this.livekitRoom.localParticipant.isScreenShareEnabled
    return this.livekitRoom?.localParticipant.setScreenShareEnabled(
      !currentState
    )
  }

  stopScreenShare() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    return this.livekitRoom?.localParticipant.setScreenShareEnabled(false)
  }

  disableVideo() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    return this.livekitRoom?.localParticipant.setCameraEnabled(false)
  }

  enableVideo() {
    if (!this.livekitRoom) return Promise.resolve(undefined)
    return this.livekitRoom?.localParticipant.setCameraEnabled(true)
  }

  isVideoEnabled() {
    return this.livekitRoom?.localParticipant.isCameraEnabled
  }

  toggleVideoForParticipant(participantId: string) {
    const participant = this.livekitRoom?.remoteParticipants.get(participantId)
    if (!participant) return

    for (const publication of participant.trackPublications.values()) {
      if (publication.kind.toString() === 'video') {
        publication.setSubscribed(!publication.isSubscribed)
      }
    }
  }

  get livekitRoom(): Room | null {
    return this.meeting.livekitRoom
  }

  get currentUser() {
    return this.meeting.currentUser
  }

  broadcastMonitor: BroadcastMonitor | null = null

  setupBroadcastMonitor() {
    if (!this.livekitRoom) {
      return
    }
    const participants = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )

    const breakoutParticipant = participants.find(
      (p) => p.identity === ServerIdentities.BreakoutLearning
    )

    // if the slide changed and we have a running broadcastMonitor,
    // we need to flush it and start a new one
    if (
      breakoutParticipant &&
      this.broadcastMonitor &&
      this.broadcastMonitor.monitoredSlide !== this.meeting.activeSlide
    ) {
      this.broadcastMonitor.flush()
      this.broadcastMonitor.detach()
      this.broadcastMonitor = new BroadcastMonitor(this.meeting)
      this.broadcastMonitor.attach()
    }
    if (breakoutParticipant && !this.broadcastMonitor) {
      this.broadcastMonitor = new BroadcastMonitor(this.meeting)
      this.broadcastMonitor.attach()
    } else if (!breakoutParticipant && this.broadcastMonitor) {
      this.broadcastMonitor.flush()
      this.broadcastMonitor.detach()
      this.broadcastMonitor = null
    }
  }

  private getAllParticipantIds() {
    const participants = new Set<string>()
    if (!this.livekitRoom) return participants

    participants.add(this.livekitRoom.localParticipant.identity)

    this.livekitRoom.remoteParticipants.forEach((participant) => {
      participants.add(participant.identity)
    })

    return participants
  }

  private onActiveSpeakersChanged(speakers: Participant[]) {
    let loudest: Participant | undefined
    let moderatorSpeaking: boolean = false
    let isSpeaking: boolean = false

    const allParticipantIds = this.getAllParticipantIds()

    speakers.forEach((speaker) => {
      // skip if not in the room - this prevents a livekit bug where
      // we get a speaker that is not in the room
      if (!allParticipantIds.has(speaker.identity)) return
      if (!loudest || speaker.audioLevel > loudest.audioLevel) loudest = speaker
      if (speaker.identity === ServerIdentities.Moderator) {
        moderatorSpeaking = true
      }
      if (speaker.identity === this.currentUser.uid) {
        isSpeaking = true
        this.speechReachabilityController.recordServerConfirmedSpeaking()
      }

      this.transcriptController.handleSpeakerChange(speaker.identity)
    })

    this.meeting.updateActiveSpeaker(loudest?.identity ?? null)
    this.meeting.setIsModeratorSpeaking(moderatorSpeaking)

    this.deadAirController.onActiveSpeakers(
      speakers
        .filter((s) => allParticipantIds.has(s.identity))
        .map((s) => s.identity)
    )

    if (isSpeaking && !this.isSpeaking) {
      if (this.speechCaptureDebounce) clearTimeout(this.speechCaptureDebounce)
      this.startedSpeakingAt = Date.now()
      this.isSpeaking = true
    } else if (!isSpeaking && this.isSpeaking) {
      if (this.speechCaptureDebounce) clearTimeout(this.speechCaptureDebounce)

      if (this.startedSpeakingAt) {
        // Calculate the duration of the speech - never assume startedSpeakingAt is 0, if it's undefined
        // (which should be impossible)
        const speechDuration = Date.now() - this.startedSpeakingAt

        // Add to the buffer
        this.durationCapturedSoFar = this.durationCapturedSoFar + speechDuration

        // give 2 seconds of margin of error - if someone speaks, then pauses and continues
        // we want to treat this as a single block of speech
        this.speechCaptureDebounce = setTimeout(() => {
          // flush the buffer
          this.meeting.logEvent('meeting_user_spoke', {
            duration: this.durationCapturedSoFar,
          })
          // reset the duration, we start over
          this.durationCapturedSoFar = 0
        }, 2000)
      }

      this.startedSpeakingAt = null
      this.isSpeaking = false
    }
  }

  private parseLivekitMessage(message: string) {
    const json = JSON.parse(message)
    const base = baseMessageSchema.parse(json)

    // i know it's redundant to return the type in the data prop as well as
    // the base.type, but it's the only way I could get the type inference
    // to work in the ifs/switches above
    switch (base.type) {
      case LivekitMessageType.status:
        return { data: statusMessageSchema.parse(json), type: base.type }
      case LivekitMessageType.duration:
        return { data: durationMessageSchema.parse(json), type: base.type }
      case LivekitMessageType.position:
        return { data: positionMessageSchema.parse(json), type: base.type }
      case LivekitMessageType.transcript:
        return { data: transcriptMessageSchema.parse(json), type: base.type }
      case LivekitMessageType.streamPosition:
        return {
          data: streamPositionMessageSchema.parse(json),
          type: base.type,
        }
      case LivekitMessageType.acknowledge:
        return { data: acknowledgeMessageSchema.parse(json), type: base.type }
      case LivekitMessageType.dragPosition:
        return {
          // no parsing, for performance reasons
          data: json as {
            id: string
            position: { x: number | undefined; y: number | undefined }
            label?: string
            isDragged: boolean
            draggedBy: string | undefined
            userId: string | undefined
            placeholder: boolean
          },

          type: base.type,
        }
      case LivekitMessageType.dragListPosition:
        return {
          // no parsing, for performance reasons
          data: json as {
            id: string
            position: {
              idBefore: string | undefined
              containerId: string | undefined
              placeholder: boolean
            }
            placeholder: boolean
          },
          type: base.type,
        }
      case LivekitMessageType.dragMousePosition:
        return {
          // no parsing, for performance reasons
          data: json as {
            id: string
            position: {
              x: number | undefined
              y: number | undefined
              anchor?: string
            }
          },
          type: base.type,
        }
      case LivekitMessageType.beforeSlideChange:
        return {
          data: beforeSlideChangeMessageSchema.parse(json),
          type: base.type,
        }
      case LivekitMessageType.simpleModerationContributionUnder:
        return {
          data: simpleModerationContributionUnderSchema.parse(json),
          type: base.type,
        }
      case LivekitMessageType.simpleModerationContributionOver:
        return {
          data: simpleModerationContributionOverSchema.parse(json),
          type: base.type,
        }
    }
    return { data: undefined, type: base.type }
  }
}

enum LivekitMessageType {
  status = 0,
  duration = 1,
  position = 2,
  transcript = 3,
  unknown = 4,
  resubscribe = 5,
  streamPosition = 6,
  streamPause = 7,
  muteMic = 8,
  acknowledge = 9,
  dragPosition = 10,
  dragMousePosition = 11,
  dragListPosition = 12,
  // optionally sent before a slide change to let the slide know
  // that it's going to leave - not all slides need it, so it's not sent
  // every time
  beforeSlideChange = 13,
  simpleModerationContributionUnder = 14,
  simpleModerationContributionOver = 15,
  // The Secretary heartbeat. Handled by the Soundcheck room adapter; listed here
  // only so the shared message parser accepts it instead of throwing a ZodError
  // on an unknown type (which surfaces as the "Application Needs Update" dialog).
  secretaryStatus = 16,
}

export enum LivekitStreamStatus {
  uninitialized = 0,
  initialized = 1,
  playing = 2,
  paused = 3,
  stopped = 4,
}

const baseMessageSchema = z.object({
  type: z.nativeEnum(LivekitMessageType),
})

const statusMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.status),
  status: z.nativeEnum(LivekitStreamStatus),
})

const durationMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.duration),
  duration: z.number().int(),
})

const positionMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.position),
  position: z.number().int(),
  duration: z.number().int(),
})

const transcriptMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.transcript),
  transcript: z.string(),
  identity: z.string(),
  id: z.number().int(), // transcriptId
  final: z.boolean(),
  finalSort: z.number().int().optional(),
})

const streamPositionMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.streamPosition),
  position: z.number().int(),
  duration: z.number().int().nullish(),
  slideId: z.string(),
  userId: z.string(),
  status: z.nativeEnum(LivekitStreamStatus),
})

const acknowledgeMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.acknowledge),
  action: z.string(),
})

const beforeSlideChangeMessageSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.beforeSlideChange),
  slideId: z.string(),
})

const simpleModerationContributionOverSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.simpleModerationContributionOver),
  identity: z.string(),
  slideId: z.string(),
})

const simpleModerationContributionUnderSchema = baseMessageSchema.extend({
  type: z.literal(LivekitMessageType.simpleModerationContributionUnder),
  identity: z.string(),
  slideId: z.string(),
})
